import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Timer, CheckCircle2, AlertTriangle, RefreshCw, 
  ArrowLeft, Award, Check, Clock, Zap, Save, Lock
} from 'lucide-react';
import { useEvent, useEventStandings } from '../hooks/useEvents';
import { usePreviewResults, usePublishResults } from '../hooks/useResults';
import { useAuth } from '../context/AuthContext';
import { usePageTracking } from '../hooks/usePageTracking';
import api from '../lib/api';

export interface RivaisPilotResult {
  id: string;
  name: string;
  username: string;
  steam_id?: string;
  car_name?: string;
  ers: number;
  position: number;
  best_lap: string;
  laps_completed: number;
  status: 'registered' | 'dns' | 'dnf' | 'dsq';
  telemetry_best_lap?: string;
  telemetry_valid?: boolean;
  telemetry_status?: 'pending' | 'confirmed' | 'rejected';
  telemetry_record_id?: string;
  telemetry_car?: string;
  telemetry_track?: string;
  telemetry_date?: string;
  preview_ers_delta?: number;
  preview_ers_after?: number;
}

const parseLapTimeToMs = (timeStr: string | undefined): number => {
  if (!timeStr || typeof timeStr !== 'string') return Infinity;
  const clean = timeStr.trim();
  if (!clean || !clean.includes(':')) return Infinity;
  const parts = clean.split(':');
  if (parts.length === 2) {
    const mins = parseFloat(parts[0]);
    const secs = parseFloat(parts[1].replace(',', '.'));
    if (isNaN(mins) || isNaN(secs)) return Infinity;
    return Math.round((mins * 60 + secs) * 1000);
  } else if (parts.length === 3) {
    const hours = parseFloat(parts[0]);
    const mins = parseFloat(parts[1]);
    const secs = parseFloat(parts[2].replace(',', '.'));
    if (isNaN(hours) || isNaN(mins) || isNaN(secs)) return Infinity;
    return Math.round((hours * 3600 + mins * 60 + secs) * 1000);
  }
  return Infinity;
};

const formatMsToLapTime = (ms: number): string => {
  if (!ms || ms === Infinity || isNaN(ms)) return '';
  const totalSecs = ms / 1000;
  const mins = Math.floor(totalSecs / 60);
  const secs = (totalSecs % 60).toFixed(3);
  return `${mins}:${secs.padStart(6, '0')}`;
};

const fmtDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

const fmtTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

const startOfDay = (d: Date): number =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

// ── Máscara de tempo: buffer de dígitos puros separado da exibição ──
// O buffer guarda apenas o que o organizador digitou (ex: "123456").
// A exibição é derivada dele ("1:23.456"), assim os zeros de preenchimento
// da máscara nunca contaminam a sequência digitada.

const digitsOf = (s: string): string => s.replace(/\D/g, '');

// "0:00.001" → "1" (remove zeros que são apenas preenchimento da máscara)
const normalizeLapBuffer = (display: string): string =>
  digitsOf(display).replace(/^0+(?=[0-9])/, '');

// Buffer → exibição MM:SS.mmm (preenche à esquerda até 6 dígitos)
const formatLapBuffer = (buf: string): string => {
  if (!buf) return '';
  const d = buf.padStart(Math.max(6, buf.length), '0');
  const ms = d.slice(-3);
  const sec = d.slice(-5, -3).padStart(2, '0');
  const min = d.slice(0, -5);
  return `${min}:${sec}.${ms}`;
};

// Posição do cursor logo após o n-ésimo dígito da string
const posAfterNthDigit = (s: string, n: number): number => {
  if (n <= 0) return 0;
  let count = 0;
  for (let i = 0; i < s.length; i++) {
    if (/\d/.test(s[i])) {
      count++;
      if (count === n) return i + 1;
    }
  }
  return s.length;
};

const normStr = (s?: string): string => (s || '').toLowerCase().trim();

export const EventResultsRivaisPage: React.FC = () => {
  const params = useParams<{ slug?: string; eventId: string }>();
  const slug = params.slug;
  const eventId = params.eventId || '';
  usePageTracking(`/events/${eventId}/results-rivais`, 'Resultados Rivais (Hotlap)');

  const navigate = useNavigate();
  const { emssUser } = useAuth();
  const isAdmin = emssUser?.roles?.includes('admin') ?? false;

  const { data: event, isLoading: loadingEvent } = useEvent(eventId);
  const { data: standingsData } = useEventStandings(eventId);

  const [sessions, setSessions] = useState<any[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [results, setResults] = useState<RivaisPilotResult[]>([]);
  const [telemetryRecords, setTelemetryRecords] = useState<any[]>([]);
  const [trialRange, setTrialRange] = useState<{ start: string; end: string } | null>(null);
  const [trackNames, setTrackNames] = useState<string[]>([]);
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftUpdatedAt, setDraftUpdatedAt] = useState<string | null>(null);

  const [previewing, setPreviewing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Refs para preservar cursor nos inputs de tempo
  const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const pendingCursor = useRef<{ pilotId: string; pos: number } | null>(null);
  // Dígitos puros digitados por piloto (fonte da verdade da máscara)
  const lapBuffers = useRef<Map<string, string>>(new Map());

  const previewMutation = usePreviewResults();
  const publishMutation = usePublishResults();

  // Carrega sessões do evento + período do time trial + nomes de pista
  useEffect(() => {
    if (!eventId) return;
    api.get(`/events/${eventId}/structure`)
      .then((res: any) => {
        // /structure retorna uma LISTA de dias (ou objeto com days/event_days)
        const days: any[] = Array.isArray(res) ? res : (res?.days || res?.event_days || []);
        const sessList: any[] = [];
        const trackSet = new Set<string>();
        const dayTimestamps: number[] = [];

        days.forEach((d: any) => {
          const dayVal = d.scheduled_at || d.scheduled_date;
          if (dayVal) {
            const ts = new Date(dayVal).getTime();
            if (!isNaN(ts)) dayTimestamps.push(ts);
          }
          const races = d.races || d.event_races || [];
          races.forEach((r: any) => {
            // Nome da pista (cruza com track_name da telemetria)
            const cfgs = r.circuit_layouts?.circuit_game_configs;
            const cfg = Array.isArray(cfgs) ? cfgs[0] : cfgs;
            const trackName = cfg?.circuits?.name || r.circuit?.name;
            if (trackName) trackSet.add(String(trackName));

            const sess = r.sessions || r.event_sessions || [];
            sess.forEach((s: any) => {
              sessList.push({ ...s, race_name: r.name || 'Disputa Rivais' });
            });
          });
        });

        if (dayTimestamps.length > 0) {
          setTrialRange({
            start: new Date(Math.min(...dayTimestamps)).toISOString(),
            end: new Date(Math.max(...dayTimestamps)).toISOString(),
          });
        }
        setTrackNames(Array.from(trackSet));
        setSessions(sessList);
        if (sessList.length > 0 && !selectedSessionId) {
          setSelectedSessionId(sessList[0].id);
        }
      })
      .catch((err) => console.error('Erro ao carregar estrutura do evento rivais:', err));
  }, [eventId]);

  // Carrega telemetria do evento (todos os status: pendente e confirmado)
  useEffect(() => {
    if (!eventId) return;
    api.get(`/telemetry/lap-records?event_id=${eventId}&status=all`)
      .then((res: any) => {
        if (Array.isArray(res)) setTelemetryRecords(res);
      })
      .catch(() => {});
  }, [eventId]);

  // Melhor registro de telemetria de um piloto, cruzando:
  // inscrito (user_id) + carro inscrito + período do time trial + pista do evento.
  const findTelemetryFor = (pilot: RivaisPilotResult): { lap: string; record: any } | null => {
    if (telemetryRecords.length === 0) return null;

    const recs = telemetryRecords.filter(t => t.user_id === pilot.id && t.status !== 'rejected');
    if (recs.length === 0) return null;

    // 1. Período do time trial (folga de 1 dia nas pontas)
    let pool = recs;
    if (trialRange) {
      const from = new Date(trialRange.start).getTime() - 86400000;
      const to = new Date(trialRange.end).getTime() + 86400000;
      const inRange = recs.filter(t => {
        if (!t.created_at) return true;
        const ts = new Date(t.created_at).getTime();
        return ts >= from && ts <= to;
      });
      if (inRange.length > 0) pool = inRange;
    }

    // 2. Carro do inscrito × carro do registro
    if (pilot.car_name) {
      const carMatch = pool.filter(t => {
        const a = normStr(t.car_name);
        const b = normStr(pilot.car_name);
        return a && b && (a.includes(b) || b.includes(a));
      });
      if (carMatch.length > 0) pool = carMatch;
    }

    // 3. Pista do evento × pista do registro
    if (trackNames.length > 0) {
      const trackMatch = pool.filter(t => trackNames.some(name => {
        const a = normStr(t.track_name);
        const b = normStr(name);
        return a && b && (a.includes(b) || b.includes(a));
      }));
      if (trackMatch.length > 0) pool = trackMatch;
    }

    const best = pool.reduce(
      (acc, r) => (!acc || (r.best_lap_ms || Infinity) < (acc.best_lap_ms || Infinity) ? r : acc),
      null as any,
    );
    if (!best || !best.best_lap_ms) return null;
    return { lap: formatMsToLapTime(best.best_lap_ms), record: best };
  };

  // Carrega inscrições/pilotos (independente de sessão) e mescla
  // rascunho + resultados já publicados quando há sessão selecionada.
  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;

    (async () => {
      try {
        const regsRes: any = await api.get(`/events/${eventId}/registrations`);
        if (cancelled) return;

        const regs = Array.isArray(regsRes) ? regsRes : (regsRes?.registrations || []);

        const mapped: RivaisPilotResult[] = regs.map((reg: any, idx: number) => {
          const pilot = reg.users || {};
          const pilotId = reg.pilot_id;
          const carName: string | undefined = reg.cars?.model || reg.car_name || undefined;

          const base: RivaisPilotResult = {
            id: pilotId,
            name: pilot.display_name || pilot.username || 'Piloto',
            username: pilot.username || pilot.display_name || 'piloto',
            steam_id: pilot.steam_id || pilot.xbox_gamertag,
            car_name: carName,
            ers: Number(pilot.current_ers ?? 1000),
            position: idx + 1,
            best_lap: '',
            laps_completed: 1,
            status: reg.status || 'registered',
            preview_ers_delta: 0,
            preview_ers_after: pilot.current_ers || 1000,
          };

          // Semente: melhor volta da telemetria já cruzada com o piloto
          const tel = findTelemetryFor(base);
          if (tel) {
            base.best_lap = tel.lap;
            base.telemetry_best_lap = tel.lap;
            base.telemetry_valid = tel.record.is_valid ?? true;
            base.telemetry_status = tel.record.status || 'pending';
            base.telemetry_record_id = tel.record.id;
            base.telemetry_car = tel.record.car_name;
            base.telemetry_track = tel.record.track_name;
            base.telemetry_date = tel.record.created_at;
          }
          return base;
        });

        let merged = sortRivaisResults(mapped);

        // Rascunho parcial (gravação ao longo do período)
        if (selectedSessionId) {
          try {
            const draft: any = await api.get(`/results/${selectedSessionId}/draft`);
            if (cancelled) return;
            const draftList = draft?.results || [];
            if (draftList.length > 0) {
              const draftMap = new Map(draftList.map((r: any) => [r.pilot_id, r]));
              merged = merged.map(p => {
                const saved: any = draftMap.get(p.id);
                if (!saved) return p;
                return {
                  ...p,
                  best_lap: saved.best_lap || p.best_lap,
                  laps_completed: Number(saved.laps_completed) || p.laps_completed,
                  status: (saved.status as RivaisPilotResult['status']) || p.status,
                };
              });
              setDraftUpdatedAt(draft?.updated_at || null);
            } else {
              setDraftUpdatedAt(draft?.updated_at || null);
            }
          } catch { /* sem rascunho */ }

          // Resultados já publicados têm precedência sobre o rascunho
          try {
            const publishedRes: any = await api.get(`/results/${selectedSessionId}`);
            if (cancelled) return;
            const published = publishedRes?.results || [];
            if (published.length > 0) {
              const pubMap = new Map(published.map((r: any) => [r.pilot_id, r]));
              merged = merged.map(p => {
                const saved: any = pubMap.get(p.id);
                if (!saved) return p;
                return {
                  ...p,
                  position: saved?.position || p.position,
                  best_lap: saved?.best_lap || p.best_lap,
                  laps_completed: Number(saved?.laps_completed) || p.laps_completed,
                  status: saved?.status || p.status,
                  preview_ers_delta: saved?.ers_delta || 0,
                  preview_ers_after: saved?.ers_after || p.ers,
                };
              });
            }
          } catch { /* sessão sem resultados */ }
        }

        if (!cancelled) setResults(sortRivaisResults(merged));
      } catch (err) {
        console.error('Erro ao carregar inscrições rivais:', err);
        if (!cancelled) setErrorMsg('Falha ao carregar lista de pilotos inscritos.');
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, telemetryRecords, selectedSessionId, trialRange, trackNames]);

  const sortRivaisResults = (list: RivaisPilotResult[]): RivaisPilotResult[] => {
    const statusRank = (s: string) => (s === 'registered' ? 0 : s === 'dnf' ? 1 : s === 'dns' ? 2 : 3);
    
    const copied = [...list];
    copied.sort((a, b) => {
      const rankA = statusRank(a.status);
      const rankB = statusRank(b.status);
      if (rankA !== rankB) return rankA - rankB;

      if (a.status === 'registered') {
        const msA = parseLapTimeToMs(a.best_lap);
        const msB = parseLapTimeToMs(b.best_lap);
        if (msA !== msB) return msA - msB; // Menor tempo primeiro
      }
      return a.name.localeCompare(b.name);
    });

    return copied.map((p, idx) => ({ ...p, position: idx + 1 }));
  };

  // ── Máscara de tempo: aplica a edição do input sobre o buffer de dígitos ──
  const handleLapTimeChange = (pilotId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const newValue = input.value;
    const cursor = input.selectionStart ?? newValue.length;
    const oldDisplay = results.find(p => p.id === pilotId)?.best_lap ?? '';

    // Sincroniza o buffer com o valor exibido (cobre carga externa: telemetria/sessão)
    let buffer = lapBuffers.current.get(pilotId) ?? '';
    if (formatLapBuffer(buffer) !== oldDisplay) buffer = normalizeLapBuffer(oldDisplay);
    const padCountOld = Math.max(0, digitsOf(oldDisplay).length - buffer.length);

    if (newValue === oldDisplay) return;

    // Diff: trecho removido/inserido entre valor antigo e novo
    let start = 0;
    while (start < oldDisplay.length && start < newValue.length && oldDisplay[start] === newValue[start]) start++;
    let endOld = oldDisplay.length;
    let endNew = newValue.length;
    while (endOld > start && endNew > start && oldDisplay[endOld - 1] === newValue[endNew - 1]) {
      endOld--;
      endNew--;
    }
    const removedDigits = digitsOf(oldDisplay.slice(start, endOld));
    const insertedDigits = digitsOf(newValue.slice(start, endNew));

    // Posição (em dígitos do buffer) onde a edição acontece
    const removedStartDigit = digitsOf(oldDisplay.slice(0, start)).length;
    const removedEndDigit = removedStartDigit + removedDigits.length;
    const bufStart = Math.max(0, Math.min(buffer.length, removedStartDigit - padCountOld));

    // Remove dígitos apagados (ignora zeros de preenchimento da máscara)
    let newBuffer = buffer;
    const rmFrom = Math.max(0, removedStartDigit - padCountOld);
    const rmTo = Math.max(0, removedEndDigit - padCountOld);
    if (rmTo > rmFrom) {
      newBuffer = buffer.slice(0, rmFrom) + buffer.slice(rmTo);
    }

    // Insere dígitos novos
    newBuffer = (newBuffer.slice(0, bufStart) + insertedDigits + newBuffer.slice(bufStart)).slice(0, 7);
    lapBuffers.current.set(pilotId, newBuffer);
    const newDisplay = formatLapBuffer(newBuffer);

    // Cursor: mapeia a posição em dígitos do buffer para a nova exibição
    const digitsBeforeCursor = digitsOf(newValue.slice(0, cursor)).length;
    const bufDigitsBeforeCursor = Math.max(0, Math.min(newBuffer.length, digitsBeforeCursor - padCountOld));
    const padNew = Math.max(0, digitsOf(newDisplay).length - newBuffer.length);
    const newPos = Math.min(newDisplay.length, posAfterNthDigit(newDisplay, padNew + bufDigitsBeforeCursor));

    inputRefs.current.set(pilotId, input);
    pendingCursor.current = { pilotId, pos: newPos };

    // Sem sort durante a digitação: reordenar moveria a linha e derrubaria o foco
    setResults(prev => prev.map(p => p.id === pilotId ? { ...p, best_lap: newDisplay } : p));
  };

  // Ao sair do campo, reordena pela melhor volta
  const handleLapTimeBlur = () => {
    setResults(prev => sortRivaisResults(prev));
  };

  // Restaura cursor antes da pintura (useLayoutEffect evita flicker)
  useLayoutEffect(() => {
    const pending = pendingCursor.current;
    if (!pending) return;
    pendingCursor.current = null;
    const el = inputRefs.current.get(pending.pilotId);
    if (el && document.activeElement === el && el.selectionStart !== pending.pos) {
      el.setSelectionRange(pending.pos, pending.pos);
    }
  }, [results]);

  const handleApplyTelemetry = (pilotId: string, telemetryLap: string) => {
    if (!telemetryLap) return;
    setResults(prev => {
      const updated = prev.map(p => p.id === pilotId ? { ...p, best_lap: telemetryLap } : p);
      return sortRivaisResults(updated);
    });
    setSuccessMsg(`Tempo de telemetria (${telemetryLap}) aplicado com sucesso.`);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  const formatResults = () =>
    results.map(r => ({
      pilot_id: r.id,
      position: r.position,
      best_lap: r.best_lap || undefined,
      laps_completed: Number(r.laps_completed) || 1,
      status: r.status,
      current_ers: r.ers,
    }));

  const handlePreview = async () => {
    if (!selectedSessionId) return;
    setPreviewing(true);
    setErrorMsg(null);
    try {
      const res: any = await previewMutation.mutateAsync({ sessionId: selectedSessionId, results: formatResults() });
      if (Array.isArray(res)) {
        setResults(prev => prev.map(p => {
          const found = res.find((x: any) => x.pilot_id === p.id);
          if (!found) return p;
          return {
            ...p,
            preview_ers_delta: found.ers_delta || 0,
            preview_ers_after: found.ers_after || p.ers,
          };
        }));
      }
      setSuccessMsg('Simulação de eRS calculada com sucesso.');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      setErrorMsg(err?.detail || err?.response?.data?.detail || 'Erro ao simular eRS.');
    } finally {
      setPreviewing(false);
    }
  };

  // Rascunho: gravação parcial SEM calcular eRS/mRCI (isolado em event_result_drafts)
  const handleSaveDraft = async () => {
    if (!selectedSessionId) return;
    setDraftSaving(true);
    setErrorMsg(null);
    try {
      const res: any = await api.put(`/results/${selectedSessionId}/draft`, { results: formatResults() });
      const count = res?.count ?? formatResults().length;
      setDraftUpdatedAt(new Date().toISOString());
      setSuccessMsg(`Rascunho salvo (${count} pilotos). Continue validando e publique no último dia do período.`);
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err: any) {
      setErrorMsg(err?.detail || err?.response?.data?.detail || 'Erro ao salvar rascunho.');
    } finally {
      setDraftSaving(false);
    }
  };

  // Publicação só no último dia do período (o backend também bloqueia)
  const canPublish = (() => {
    if (!trialRange) return true;
    const lastDay = new Date(trialRange.end);
    return startOfDay(new Date()) >= startOfDay(lastDay);
  })();

  const handlePublish = async () => {
    if (!selectedSessionId) return;
    if (!canPublish) {
      setErrorMsg(`A publicação de eventos Rivais fica disponível apenas no último dia do período (${trialRange ? fmtDate(trialRange.end) : ''}).`);
      return;
    }
    if (!window.confirm('Deseja publicar e encerrar definitivamente este evento Rivais? O eRS e mRCI serão calculados e aplicados para todos os participantes.')) {
      return;
    }
    setPublishing(true);
    setErrorMsg(null);
    try {
      await publishMutation.mutateAsync({ sessionId: selectedSessionId, results: formatResults() });
      setSuccessMsg('Resultados publicados com sucesso! eRS e mRCI atualizados.');
      setTimeout(() => {
        const detailsPath = slug ? `/leagues/${slug}/events/${eventId}` : `/events/${eventId}`;
        navigate(detailsPath);
      }, 2000);
    } catch (err: any) {
      setErrorMsg(err?.detail || err?.response?.data?.detail || 'Erro ao publicar resultados.');
    } finally {
      setPublishing(false);
    }
  };

  if (loadingEvent) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0a0b0f] text-[#98c136]">
        <RefreshCw className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const backPath = slug ? `/leagues/${slug}/events/${eventId}` : `/events/${eventId}`;

  return (
    <div className="min-h-screen bg-[#0a0b0f] text-white px-4 md:px-10 py-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <button
            onClick={() => navigate(backPath)}
            className="flex items-center gap-2 text-zinc-400 hover:text-white mb-3 text-sm transition-colors"
          >
            <ArrowLeft size={16} /> Voltar para o Evento
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#E67E22]/20 border border-[#E67E22]/40 flex items-center justify-center text-[#E67E22]">
              <Timer size={22} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="bg-[#E67E22]/20 text-[#E67E22] border border-[#E67E22]/40 text-xs px-2.5 py-0.5 rounded-full font-semibold uppercase tracking-wider flex items-center gap-1">
                  <Zap size={12} /> Rivais (Hotlap & Telemetria)
                </span>
                {trialRange ? (
                  <span className="text-xs text-zinc-400 flex items-center gap-1">
                    <Clock size={12} /> Time trial aberto de {fmtDate(trialRange.start)} até {fmtDate(trialRange.end)}
                  </span>
                ) : (
                  <span className="text-xs text-zinc-400">Janela de Apuração Aberta</span>
                )}
              </div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white mt-1">
                {event?.name || 'Apuração de Rivais'}
              </h1>
            </div>
          </div>
        </div>

        {/* Ações principais */}
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={handleSaveDraft}
            disabled={draftSaving || !selectedSessionId || results.length === 0}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl text-sm font-medium flex items-center gap-2 border border-zinc-700 transition-all disabled:opacity-50"
            title="Grava o lançamento parcial sem calcular eRS/mRCI"
          >
            {draftSaving ? <RefreshCw size={15} className="animate-spin" /> : <Save size={15} />}
            Salvar Rascunho
          </button>

          <button
            onClick={handlePreview}
            disabled={previewing || !selectedSessionId || results.length === 0}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl text-sm font-medium flex items-center gap-2 border border-zinc-700 transition-all disabled:opacity-50"
          >
            {previewing ? <RefreshCw size={15} className="animate-spin" /> : <Timer size={15} />}
            Simular eRS
          </button>

          <button
            onClick={handlePublish}
            disabled={publishing || !selectedSessionId || results.length === 0 || !canPublish}
            className="px-5 py-2 bg-[#98c136] hover:bg-[#86ac2f] text-[#0a0b0f] font-bold rounded-xl text-sm flex items-center gap-2 shadow-lg shadow-[#98c136]/20 transition-all disabled:opacity-50"
            title={canPublish ? 'Publicar e encerrar' : `Disponível no último dia (${trialRange ? fmtDate(trialRange.end) : ''})`}
          >
            {publishing ? <RefreshCw size={15} className="animate-spin" /> : canPublish ? <CheckCircle2 size={15} /> : <Lock size={15} />}
            Publicar & Encerrar Evento
          </button>
        </div>
      </div>

      {/* Alertas */}
      <div className="max-w-7xl mx-auto">
        {successMsg && (
          <div className="mb-6 p-4 rounded-xl bg-[#98c136]/10 border border-[#98c136]/30 text-[#98c136] flex items-center gap-3 text-sm">
            <CheckCircle2 size={18} /> {successMsg}
          </div>
        )}
        {errorMsg && (
          <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 flex items-center gap-3 text-sm">
            <AlertTriangle size={18} /> {errorMsg}
          </div>
        )}

        {/* Info card: período + estado do lançamento */}
        <div className="bg-[#12131c] border border-[#E67E22]/30 rounded-2xl p-5 mb-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-[#E67E22]/10 text-[#E67E22] mt-0.5">
              <Clock size={20} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Processo Funcional Rivais</h3>
              <p className="text-xs text-zinc-400 mt-0.5">
                As posições ajustam-se automaticamente pela <b>Melhor Volta</b> (ordem ascendente). Valide os tempos ao
                longo do período com <b>Salvar Rascunho</b>; a publicação (eRS/mRCI) acontece apenas no último dia.
                A telemetria é cruzada por <b>período + pista + inscrito + carro</b>.
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 text-xs font-mono">
            <div className="bg-zinc-900 border border-zinc-800 px-3 py-2 rounded-xl text-zinc-300">
              Sessão: {sessions.find(s => s.id === selectedSessionId)?.race_name || 'Sessão Única'}
            </div>
            {trialRange && (
              <div className="bg-[#E67E22]/10 border border-[#E67E22]/30 px-3 py-2 rounded-xl text-[#E67E22]">
                Período: {fmtDate(trialRange.start)} → {fmtDate(trialRange.end)}
              </div>
            )}
            <div className={`px-3 py-2 rounded-xl border ${draftUpdatedAt ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-zinc-900 border-zinc-800 text-zinc-500'}`}>
              {draftUpdatedAt ? `Rascunho salvo às ${fmtTime(draftUpdatedAt)}` : 'Nenhum rascunho salvo'}
            </div>
            {!canPublish && (
              <div className="bg-amber-500/10 border border-amber-500/30 text-amber-400 px-3 py-2 rounded-xl flex items-center gap-1.5">
                <Lock size={12} /> Publicação em {trialRange ? fmtDate(trialRange.end) : '—'}
              </div>
            )}
          </div>
        </div>

        {/* Tabela de Resultados Rivais */}
        <div className="bg-[#12131c] border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-zinc-800 text-[11px] font-mono text-zinc-400 uppercase tracking-wider bg-zinc-900/50">
                  <th className="py-4 px-5">Pos</th>
                  <th className="py-4 px-5">Gamertag</th>
                  <th className="py-4 px-5">Melhor Volta (Manual / Jogo)</th>
                  <th className="py-4 px-5">Telemetria Agente</th>
                  <th className="py-4 px-5">Simulação eRS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/60 text-sm">
                {results.map((pilot, idx) => {
                  const tel = findTelemetryFor(pilot);
                  const telLap = tel?.lap;
                  const telRecord = tel?.record;
                  return (
                    <tr key={pilot.id} className="hover:bg-zinc-800/20 transition-colors">
                      <td className="py-4 px-5 font-mono font-bold text-white">
                        <div className="flex items-center gap-2">
                          {idx === 0 && <Award size={16} className="text-amber-400" />}
                          {idx === 1 && <Award size={16} className="text-zinc-300" />}
                          {idx === 2 && <Award size={16} className="text-amber-700" />}
                          <span>#{pilot.position}</span>
                        </div>
                      </td>
                      <td className="py-4 px-5 font-medium text-white">
                        <div>{pilot.steam_id || pilot.name}</div>
                        <div className="text-xs text-zinc-400 font-mono">
                          {pilot.steam_id ? pilot.name : `@${pilot.username}`}
                        </div>
                      </td>
                      <td className="py-4 px-5">
                        <input
                          ref={(el) => { if (el) inputRefs.current.set(pilot.id, el); }}
                          type="text"
                          value={pilot.best_lap}
                          onChange={(e) => handleLapTimeChange(pilot.id, e)}
                          onBlur={handleLapTimeBlur}
                          placeholder="0:00.000"
                          className="bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-1.5 w-32 font-mono text-sm text-white focus:outline-none focus:border-[#98c136]"
                        />
                      </td>
                      <td className="py-4 px-5 font-mono">
                        {telLap ? (
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <span className="text-[#98c136] bg-[#98c136]/10 px-2 py-1 rounded-lg text-xs">
                                {telLap}
                              </span>
                              <button
                                onClick={() => handleApplyTelemetry(pilot.id, telLap)}
                                className="text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 px-2 py-1 rounded-lg border border-zinc-700 transition-colors flex items-center gap-1"
                                title="Aplicar tempo da telemetria"
                              >
                                <Check size={12} /> Usar
                              </button>
                            </div>
                            <span className="text-[10px] text-zinc-500 font-sans">
                              {[telRecord?.car_name, telRecord?.track_name, telRecord?.created_at ? fmtDate(telRecord.created_at) : null]
                                .filter(Boolean).join(' • ')}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-zinc-400 italic">Sem telemetria</span>
                        )}
                      </td>
                      <td className="py-4 px-5 font-mono">
                        {pilot.preview_ers_delta !== undefined && pilot.preview_ers_delta !== 0 ? (
                          <span className={pilot.preview_ers_delta > 0 ? 'text-[#98c136]' : 'text-red-400'}>
                            {pilot.preview_ers_delta > 0 ? `+${pilot.preview_ers_delta}` : pilot.preview_ers_delta}
                            <span className="text-xs text-zinc-500 ml-1">({pilot.preview_ers_after})</span>
                          </span>
                        ) : (
                          <span className="text-zinc-500 text-xs italic">Aguardando simulação</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {results.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-10 text-center text-zinc-500 text-sm">
                      Nenhum piloto inscrito neste evento.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
